import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { loadAllArticles } from '@/services/articleLoader';

import { BlogCategoryFilter } from '@/components/BlogCategoryFilter';
import { Footer } from '@/components/Footer';
import { GridBackground } from '@/components/GridBackground';
import { MainNavigation } from '@/components/MainNavigation';
import { Clock, ChevronRight, ChevronLeft, Search, Home, Star, Globe, Tv, MapPin, Shield, Zap, Hammer, RefreshCw, Smartphone, Video } from 'lucide-react';
import { getArticleImages, getOptimizedSearchQuery, getPlaceholderImage } from '@/services/imageService';
import { getCurrentMonthYear } from '@/services/eliteSummaryGenerator';
import { PromoCard } from '@/components/Blog/PromoCard';
import { useGeoLocation } from '@/hooks/useGeoLocation';

// --- PROMO POOL CONFIGURATION ---
const getPromoPool = (countryCode) => {
  // Smart Geo-Targeting Logic
  let regionalLink = '/blog/regions';
  let regionalTitle = 'Global Regions Hub';

  if (countryCode === 'US') {
    regionalLink = '/blog/best-iptv-usa-2026';
    regionalTitle = 'Best IPTV in USA 🇺🇸';
  } else if (countryCode === 'CA') {
    regionalLink = '/regions/canada'; // Assuming route exists or fallback
    regionalTitle = 'Best IPTV in Canada 🇨🇦';
  } else if (countryCode === 'UK') {
    regionalLink = '/regions/uk';
    regionalTitle = 'Best IPTV in UK 🇬🇧';
  }

  return [
    // 🥇 Tier 1: Flagship Clusters
    {
      type: 'promo',
      id: 'promo-1',
      title: 'Best IPTV Service 2026',
      description: 'The definitive ranking of top verified providers. Speed, pricing, and reliability benchmarked.',
      label: 'Verified Benchmark',
      link: '/blog/best-iptv-usa-2026',
      gradient: 'from-amber-500 to-orange-600',
      icon: Star,
      tag: 'Editor Choice',
      ctaText: 'View Ranking'
    },
    {
      type: 'promo',
      id: 'promo-2',
      title: 'Unlock Firestick Developer Options',
      description: 'Essential hack to sideload apps on any Fire TV device. Step-by-step guide.',
      label: 'Essential Hack',
      link: '/blog/enable-developer-options-fire-stick',
      gradient: 'from-fuchsia-600 to-pink-600',
      icon: Zap,
      tag: 'Most Read',
      ctaText: 'Unlock Now'
    },
    // 🥈 Tier 2: Tools & Utilities
    {
      type: 'promo',
      id: 'promo-3',
      title: 'M3U to Xtream Converter',
      description: 'Extract username & password from any M3U link instantly. 100% Client-side secure.',
      label: 'Free Tool',
      link: '/tools/m3u-to-xtream',
      gradient: 'from-violet-600 to-indigo-600',
      icon: RefreshCw,
      tag: 'Instant Tool',
      ctaText: 'Convert File'
    },
    {
      type: 'promo',
      id: 'promo-4',
      title: 'Start 24h Free Trial',
      description: 'Experience premium 4K streaming without commitment. Instant activation.',
      label: 'Try Premium',
      link: 'https://xtreamcube.shop/freetrialiptv/',
      gradient: 'from-emerald-500 to-teal-600',
      icon: Shield,
      tag: 'Limited Spots',
      isExternal: true,
      ctaText: 'Get Access'
    },
    // 🥉 Tier 3: Strategic & Regional
    {
      type: 'promo',
      id: 'promo-5',
      title: regionalTitle,
      description: 'Find the best legal IPTV providers available in your specific region.',
      label: 'Local Guide',
      link: regionalLink,
      gradient: 'from-blue-500 to-cyan-600',
      icon: Globe,
      tag: 'Geo-Targeted',
      ctaText: 'Find Providers'
    },
    {
      type: 'promo',
      id: 'promo-6',
      title: 'IPTV Legality Guide 2026',
      description: 'Understanding the laws of streaming. Stay safe and compliant globally.',
      label: 'Legal Safety',
      link: '/blog/iptv-legal-usa',
      gradient: 'from-slate-700 to-slate-900',
      icon: Shield,
      tag: 'Must Read',
      ctaText: 'Read Safety Guide'
    }
  ];
};

export const BlogPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [featuredImages, setFeaturedImages] = useState({});
  const [imagesLoading, setImagesLoading] = useState(true);
  const [dynamicDate] = useState(getCurrentMonthYear());
  const [loadedArticles, setLoadedArticles] = useState([]);

  // Dynamic Injection Logic
  const { country: countryCode } = useGeoLocation();
  const PROMO_POOL = React.useMemo(() => getPromoPool(countryCode), [countryCode]);

  const articlesGridRef = React.useRef(null);

  // Load articles from /articles/ folder on mount
  useEffect(() => {
    const loadArticles = async () => {
      try {
        const articles = await loadAllArticles();
        if (articles && articles.length > 0) {
          setLoadedArticles(articles);
        } else {
          setLoadedArticles([]);
        }
      } catch (error) {
        setLoadedArticles([]);
      }
    };
    loadArticles();
  }, []);

  // Fetch images for featured articles
  useEffect(() => {
    const fetchFeaturedImages = async () => {
      if (loadedArticles.length === 0) return;

      setImagesLoading(true);
      const images = {};

      try {
        const featuredArticles = loadedArticles.slice(0, 3);

        const fetchWithTimeout = async (searchQuery, timeout = 5000) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          try {
            const result = await Promise.race([
              getArticleImages(searchQuery, 5),
              new Promise((_, reject) => timeoutId && setTimeout(() => reject(new Error('Image fetch timeout')), timeout))
            ]);
            clearTimeout(timeoutId);
            return result;
          } catch (error) {
            clearTimeout(timeoutId);
            throw error;
          }
        };

        for (let i = 0; i < featuredArticles.length; i++) {
          const article = featuredArticles[i];
          const searchQuery = getOptimizedSearchQuery(article.title);

          try {
            const fetchedImages = await fetchWithTimeout(searchQuery, 8000);

            if (fetchedImages && fetchedImages.length > i) {
              images[article.id] = fetchedImages[i];
            } else if (fetchedImages && fetchedImages.length > 0) {
              images[article.id] = fetchedImages[0];
            } else {
              images[article.id] = {
                url: null,
                placeholder: true,
                color: 'from-primary to-primary/70'
              };
            }
          } catch (error) {
            images[article.id] = {
              url: null,
              placeholder: true,
              color: 'from-primary to-primary/70'
            };
          }
        }

        setFeaturedImages(images);
      } catch (error) {
        // Silent fail
      } finally {
        setImagesLoading(false);
      }
    };

    fetchFeaturedImages();
  }, [loadedArticles]);

  const categories = ['All', ...new Set(loadedArticles.map(article => article.category))];
  const editorPicks = loadedArticles.filter(article => article.editorPick === true).slice(0, 5);

  const filteredArticles = loadedArticles.filter(article => {
    const matchCategory = selectedCategory === 'All' || article.category === selectedCategory;
    const matchSearch = (article.title && article.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (article.excerpt && article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchCategory && matchSearch;
  });

  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const currentArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);

  // --- ORGANIC GRID MIXING LOGIC ---
  const mixedContent = React.useMemo(() => {
    if (currentArticles.length === 0) return [];

    const mixed = [];
    const chunkSize = 5;
    let promoPoolIndex = 0;

    for (let i = 0; i < currentArticles.length; i += chunkSize) {
      const chunk = currentArticles.slice(i, i + chunkSize).map(a => ({ type: 'article', data: a }));

      if (chunk.length === chunkSize) {
        const promoItem = PROMO_POOL[promoPoolIndex % PROMO_POOL.length];

        // Deterministic insertion logic
        // Pattern: 2, 4, 1, 3, 0, 5 (rotates based on chunk index)
        const patterns = [2, 4, 1, 3, 0];
        const insertIndex = patterns[(i / chunkSize) % patterns.length];

        chunk.splice(insertIndex, 0, { type: 'promo', data: promoItem });
        promoPoolIndex++;
      }
      mixed.push(...chunk);
    }
    return mixed;
  }, [currentArticles, PROMO_POOL]);

  // Get featured image for display
  const getFeaturedImage = (article) => {
    const imageData = featuredImages[article.id];

    if (!imageData) {
      return { url: null, placeholder: true, color: 'from-primary to-primary/70' };
    }

    return imageData;
  };

  return (
    <>
      <Helmet>
        <title>Official IPTV Resource Center | Guides, Reviews & Benchmarks 2026</title>
        <meta name="description" content="The #1 Knowledge Hub for IPTV. Verified reviews of the best IPTV services 2026, 4K live streaming configurations, and device installation guides (Firestick, Windows, Android)." />
        <meta name="keywords" content="best iptv service 2026, 4k live iptv, iptv free trial, best iptv player for windows, iptv for firestick, tv without cable, iptv reviews, xtreamgate, iptv setup guide" />
      </Helmet>

      {/* Main Navigation */}
      <MainNavigation />

      <div className="min-h-screen relative">
        <GridBackground />
        {/* Main Blog Container - Boxed Design */}
        <div className="px-4 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            {/* Inner boxed container */}
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-card/40 to-card/20 backdrop-blur-md overflow-hidden">

              {/* HERO SECTION with Elite Search */}
              <section className="relative pt-16 pb-12 px-6 md:px-8">
                {/* XTREAMGATE Branding - Small top label */}
                <div className="text-center mb-6">
                  <p className="text-sm sm:text-base text-primary font-semibold tracking-wider uppercase">
                    Blog & Resource Center
                  </p>
                </div>


                <div className="relative z-10 max-w-5xl mx-auto text-center">
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                      <span className="text-primary">X</span>treamGate <span className="text-foreground/80">Knowledge Hub</span>
                    </h1>
                    <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-6 leading-relaxed">
                      Your elite resource for <strong className="text-white">4K Live IPTV</strong>, <strong className="text-white">verified provider reviews</strong>, and <strong className="text-white">master-class device setups</strong>.
                    </p>

                    {/* Trust Signal */}
                    <div className="flex items-center justify-center gap-2 mb-10 text-sm text-muted-foreground/80">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> 27,500+ Active Users</span>
                      <span>•</span>
                      <span>Updated Daily</span>
                      <span>•</span>
                      <span>Official Benchmarks</span>
                    </div>



                    {/* ELITE NAVIGATION CARDS */}
                    <div className="max-w-7xl mx-auto mb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-2">
                      {/* Card 1: Featured */}
                      <Link to="/blog/featured-articles" className="group relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />
                        <div className="p-6 relative z-10 flex flex-col items-center text-center">
                          <div className="mb-4 p-3 rounded-full bg-primary/10 text-primary border border-primary/20 group-hover:scale-110 transition duration-300">
                            <Star className="w-6 h-6" />
                          </div>
                          <h3 className="text-xl font-bold mb-2 text-white group-hover:text-primary transition">Featured Collection</h3>
                          <p className="text-sm text-muted-foreground mb-4">Curated top-tier guides and essential reading for 2026.</p>
                          <span className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-1">
                            Browse All <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>
                      </Link>

                      {/* Card 2: Regions */}
                      <Link to="/blog/regions" className="group relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 hover:border-blue-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/20">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />
                        <div className="p-6 relative z-10 flex flex-col items-center text-center">
                          <div className="mb-4 p-3 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 group-hover:scale-110 transition duration-300">
                            <Globe className="w-6 h-6" />
                          </div>
                          <h3 className="text-xl font-bold mb-2 text-white group-hover:text-blue-400 transition">Global Regions</h3>
                          <p className="text-sm text-muted-foreground mb-4">IPTV availability, legality, and guides for your location.</p>
                          <span className="text-xs font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1">
                            Select Region <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>
                      </Link>

                      {/* Card 3: Devices */}
                      <Link to="/blog/devices" className="group relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 hover:border-purple-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/20">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />
                        <div className="p-6 relative z-10 flex flex-col items-center text-center">
                          <div className="mb-4 p-3 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 group-hover:scale-110 transition duration-300">
                            <Tv className="w-6 h-6" />
                          </div>
                          <h3 className="text-xl font-bold mb-2 text-white group-hover:text-purple-400 transition">Device Lab</h3>
                          <p className="text-sm text-muted-foreground mb-4">Setup guides for FireStick, Smart TV, Android, and iOS.</p>
                          <span className="text-xs font-bold text-purple-400 uppercase tracking-wider flex items-center gap-1">
                            Start Setup <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>
                      </Link>

                      {/* Card 4: Sports Hub */}
                      <Link to="/sports" className="group relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 hover:border-red-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-500/20">
                        <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />
                        <div className="p-6 relative z-10 flex flex-col items-center text-center">
                          <div className="mb-4 p-3 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 group-hover:scale-110 transition duration-300 relative">
                            <Video className="w-6 h-6" />
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          </div>
                          <h3 className="text-xl font-bold mb-2 text-white group-hover:text-red-400 transition">Sports Hub</h3>
                          <p className="text-sm text-muted-foreground mb-4">Live scores, standings, and 4K streaming guides.</p>
                          <span className="text-xs font-bold text-red-400 uppercase tracking-wider flex items-center gap-1">
                            View Scores <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>
                      </Link>
                    </div>

                    <div className="max-w-7xl mx-auto mb-10">
                      <BlogCategoryFilter
                        selectedCategory={selectedCategory}
                        onCategoryChange={(category) => {
                          setSelectedCategory(category);
                          setCurrentPage(1);
                        }}
                      />
                    </div>

                    {/* ELITE SEARCH BAR & RESET (Moved Here) */}
                    <div className="max-w-3xl mx-auto mb-16 px-4">
                      <div className="flex flex-col md:flex-row items-center gap-4">
                        {/* Search Field */}
                        <div className="relative group w-full flex-1">
                          <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/50 to-blue-600/50 rounded-full blur opacity-20 group-hover:opacity-40 transition duration-500" />
                          <div className="relative flex items-center bg-white/5 backdrop-blur-xl border border-white/10 rounded-full p-1.5 shadow-2xl transition-all duration-300 group-hover:border-primary/30 group-hover:bg-white/10">
                            <div className="pl-4 pr-3 text-primary">
                              <Search className="w-5 h-5" />
                            </div>
                            <input
                              type="text"
                              placeholder="Search official documentation, error codes, & setup guides..."
                              value={searchTerm}
                              onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                              }}
                              className="flex-1 bg-transparent border-none text-base text-white placeholder-white/40 focus:outline-none focus:ring-0 px-2 h-10 w-full"
                            />
                            {searchTerm && (
                              <button
                                onClick={() => setSearchTerm('')}
                                className="p-1.5 hover:bg-white/10 rounded-full transition mr-1"
                              >
                                <span className="sr-only">Clear</span>
                                <div className="w-4 h-4 flex items-center justify-center text-white/50 hover:text-white">✕</div>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* "View All Articles" Reset Button (Adjacent) */}
                        <button
                          onClick={() => {
                            setSearchTerm('');
                            setSelectedCategory('All');
                            setCurrentPage(1);
                          }}
                          className="whitespace-nowrap px-8 py-3.5 rounded-full bg-gradient-to-r from-primary to-green-600 hover:from-primary/90 hover:to-green-600/90 text-sm font-bold text-white transition-all shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-[1.02] flex items-center gap-2 group w-full md:w-auto justify-center"
                        >
                          <Home className="w-4 h-4 text-white group-hover:scale-110 transition-transform" />
                          View All Articles
                        </button>
                      </div>
                    </div>

                  </motion.div>
                </div>
              </section>

              {/* Articles Grid (Moved UP per Elite Layout Request) */}
              <section ref={articlesGridRef} className="py-10 px-6 md:px-8">
                <div className="max-w-7xl mx-auto">
                  {mixedContent.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {mixedContent.map((item, index) => {

                        // --- RENDER PROMO ---
                        if (item.type === 'promo') {
                          return (
                            <PromoCard
                              key={`promo-${index}`}
                              {...item.data}
                              delay={index * 0.05}
                            />
                          );
                        }

                        // --- RENDER ARTICLE ---
                        const article = item.data;
                        return (
                          <motion.article
                            key={article.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                            className="group glass rounded-xl hover:bg-white/10 transition overflow-hidden"
                          >
                            <Link to={article.canonicalPath || `/blog/${article.slug}`} className="block h-full p-6">
                              {/* Category Badge */}
                              <div className="mb-3">
                                <span className="inline-block px-3 py-1 bg-primary/20 border border-primary/30 rounded-full text-xs font-semibold text-primary">
                                  {article.category}
                                </span>
                              </div>

                              {/* Title */}
                              <h3 className="text-lg font-bold mb-2 line-clamp-2 group-hover:text-primary transition">
                                {article.title}
                              </h3>

                              {/* Excerpt */}
                              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                {article.excerpt}
                              </p>

                              {/* Meta Info */}
                              <div className="flex items-center justify-between mb-4 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {article.readTime}
                                </div>
                                <span>{article.date}</span>
                              </div>

                              {/* Read More Link (Visual only) */}
                              <span
                                className="inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition"
                              >
                                Read More
                                <ChevronRight className="w-4 h-4" />
                              </span>
                            </Link>
                          </motion.article>
                        );
                      })}
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-12"
                    >
                      <p className="text-lg text-muted-foreground">No articles found. Try different search terms or categories.</p>
                    </motion.div>
                  )}
                </div>
              </section>

              {/* Pagination (Moved UP with Grid) */}
              {/* Pagination (Moved UP with Grid) */}
              {totalPages > 1 && (
                <section className="py-8 px-6 md:px-8">
                  <div className="max-w-6xl mx-auto flex justify-center gap-2 items-center">

                    {/* Previous Page Arrow */}
                    <motion.button
                      onClick={() => {
                        const newPage = Math.max(1, currentPage - 1);
                        setCurrentPage(newPage);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg transition ${currentPage === 1 ? 'opacity-30 cursor-not-allowed text-muted-foreground' : 'hover:bg-white/10 text-primary'}`}
                      whileHover={currentPage !== 1 ? { scale: 1.1 } : {}}
                      whileTap={currentPage !== 1 ? { scale: 0.9 } : {}}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </motion.button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <motion.button
                        key={page}
                        onClick={() => {
                          setCurrentPage(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-10 h-10 rounded-lg font-semibold transition flex items-center justify-center ${currentPage === page
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card/50 border border-white/10 text-foreground hover:border-primary/50'
                          }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {page}
                      </motion.button>
                    ))}

                    {/* Next Page Arrow */}
                    <motion.button
                      onClick={() => {
                        const newPage = Math.min(totalPages, currentPage + 1);
                        setCurrentPage(newPage);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg transition ${currentPage === totalPages ? 'opacity-30 cursor-not-allowed text-muted-foreground' : 'hover:bg-white/10 text-primary'}`}
                      whileHover={currentPage !== totalPages ? { scale: 1.1 } : {}}
                      whileTap={currentPage !== totalPages ? { scale: 0.9 } : {}}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </motion.button>

                  </div>
                </section>
              )}

              {/* Old Sections Removed in favor of Elite Cards */}


              {/* Articles Grid */}
              {/* Articles Grid (Restored to Full Width) */}


              {/* Elite Next Step Bar - Restored & Upgraded */}
              <section className="py-12 px-6 md:px-8 bg-gradient-to-br from-card/10 to-background">
                <div className="max-w-5xl mx-auto">
                  <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 md:p-12 shadow-2xl">
                    {/* Background Glows */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
                      <div className="max-w-xl">
                        <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white/70 mb-4">
                          Ready to Upgrade Your Experience?
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                          Join 27,000+ happy viewers today. Get instant access to 20,000+ channels and our massive VOD library.
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                        <a
                          href="https://xtreamcube.shop/freetrialiptv/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-blue-600 rounded-xl font-bold text-white shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto min-w-[200px]"
                        >
                          <span className="relative z-10">Get Free Trial</span>
                          <ChevronRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                          <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition duration-500 rounded-xl" />
                        </a>

                        <Link
                          to="/about"
                          className="px-8 py-4 rounded-xl border border-white/10 hover:bg-white/5 font-semibold text-white transition w-full sm:w-auto min-w-[160px] text-center"
                        >
                          Why Us?
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Closing inner boxed container */}
            </div>
          </div>
        </div>
      </div>

      {/* MAGICAL FLOATING SIDEBAR - DEACTIVATED */}
      <AnimatePresence>
        {false && (
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 100 }}
            className="fixed right-24 top-1/2 -translate-y-1/2 z-50 hidden xl:flex flex-col gap-4 w-80"
          >
            <div className="glass-card p-5 rounded-2xl border border-primary/30 bg-black/60 backdrop-blur-xl shadow-2xl relative overflow-hidden ring-1 ring-white/10">
              {/* Elite Gold/Primary Gradient Line */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-500 via-primary to-blue-600" />

              <div className="flex items-center gap-2 mb-6 border-b border-white/5 pb-4">
                <div className="p-1.5 rounded-lg bg-yellow-500/10 text-yellow-500 border border-yellow-500/20">
                  <span className="text-lg">⭐</span>
                </div>
                <div>
                  <h3 className="font-bold text-white tracking-wide text-sm uppercase">Editor's Choice</h3>
                  <p className="text-[10px] text-muted-foreground">Curated for maximum value</p>
                </div>
              </div>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 custom-scrollbar">
                {editorPicks.map((pick, idx) => (
                  <Link to={`/blog/${pick.slug}`} key={pick.id} className="block group">
                    <div className="flex gap-3 items-center p-2 rounded-lg hover:bg-white/5 transition border border-transparent hover:border-white/5">
                      <div className="w-12 h-12 rounded-lg bg-card/50 overflow-hidden flex-shrink-0 border border-white/10 group-hover:border-primary/50 transition shadow-lg relative">
                        {/* Replaced Purple with Blue/Green/Gold logic */}
                        <div className={`w-full h-full bg-gradient-to-br ${idx === 0 ? 'from-yellow-500/20' :
                          idx === 1 ? 'from-green-500/20' :
                            idx === 2 ? 'from-blue-500/20' :
                              'from-gray-500/20'} to-transparent`} />
                        <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold leading-tight mb-1 group-hover:text-primary transition line-clamp-2 text-white/90">
                          {pick.title}
                        </h4>
                        <span className="text-[10px] text-muted-foreground/80 font-mono flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {pick.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* CTA BLOCK */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <a
                  href="https://xtreamcube.shop/freetrialiptv/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block w-full overflow-hidden rounded-xl bg-gradient-to-br from-primary via-primary/80 to-blue-600 p-4 text-center transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/20"
                >
                  <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition duration-500" />
                  <div className="relative z-10">
                    <h4 className="font-bold text-white text-sm mb-1">XtreamCube Premium</h4>
                    <p className="text-[10px] text-white/90 mb-3 font-medium">Get your 24h Free Trial Today</p>
                    <span className="inline-block bg-black/30 backdrop-blur-md rounded-lg px-3 py-1.5 text-xs font-bold text-white border border-white/10 group-hover:bg-white group-hover:text-primary transition-colors">
                      Instant Access &rarr;
                    </span>
                  </div>
                </a>
              </div>
            </div >
          </motion.div >
        )}
      </AnimatePresence>
      <Footer />
    </>
  );
};
